import { Helmet } from "react-helmet-async";
import { CANADA_KEYWORDS_FOR_META } from "../../lib/data/canadaKeywords";

/**
 * Per-route SEO via react-helmet-async.
 *
 * Writes title, description, canonical, Open Graph, Twitter, geo and JSON-LD
 * into the <head> on route change. Canonical is REQUIRED and should be a
 * self-referencing absolute URL for the current route.
 *
 * Prerender script (/scripts/prerender.js) stamps the same values into
 * build/<route>/index.html so bots that don't execute JS still see them.
 *
 * No static canonical lives in public/index.html — this component owns it.
 */
export default function Seo({
  title,
  description,
  keywords = [],
  canonical,
  image,
  jsonLd,
  type = "website",
}) {
  const kw = Array.from(new Set([...(keywords || []), ...CANADA_KEYWORDS_FOR_META])).join(", ");

  return (
    <Helmet prioritizeSeoTags>
      {title && <title>{title}</title>}
      {description && <meta name="description" content={description} />}
      {kw && <meta name="keywords" content={kw} />}
      {canonical && <link rel="canonical" href={canonical} />}
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1" />
      <meta name="geo.region" content="CA-BC" />
      <meta name="geo.placename" content="British Columbia, Canada" />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:site_name" content="Maze Security Solutions" />
      {title && <meta property="og:title" content={title} />}
      {description && <meta property="og:description" content={description} />}
      {canonical && <meta property="og:url" content={canonical} />}
      {image && <meta property="og:image" content={image} />}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      {title && <meta name="twitter:title" content={title} />}
      {description && <meta name="twitter:description" content={description} />}
      {image && <meta name="twitter:image" content={image} />}

      {jsonLd && (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      )}
    </Helmet>
  );
}
